import type { GraphNodeShapeDescriptor } from '../Cmarkdowngraphcontrols';
import type { FlowchartHoverState } from './flowchart-context';
import { TRANSITION_DURATION } from './flowchart-constants';
import {
  buildFlowchartBoxShadow,
  getFlowchartNodeOpacity,
  resolveFlowchartIconColor,
  resolveFlowchartNodeColors,
  resolveFlowchartRoleColors,
} from './flowchart-node-style';
import { getFlowchartShapeDescriptor, shapeSupportsInsetRings } from './flowchart-shapes';
import type { FlowchartNodeData } from './flowchart-types';
import { FlowchartLabelOrigin, FlowchartNodeRole } from './parse-mermaid-flowchart';

const REST_SCALE = 'scale(0.95)';
const RESTING_NODE_SCALE = 1;
const EMPHASIZED_NODE_SCALE = 1.03;
const HIDDEN_OPACITY = 0;
const RESTING_BORDER_WIDTH = 1.5;
const EMPHASIZED_BORDER_WIDTH = 2;
const FLOWCHART_NODE_TRANSITION = [
  `opacity ${TRANSITION_DURATION} ease-out`,
  `transform ${TRANSITION_DURATION} ease-out`,
  `box-shadow ${TRANSITION_DURATION} ease-out`,
].join(', ');

interface FlowchartNodeActivity {
  isActive: boolean;
  isEmphasized: boolean;
  isSelected: boolean;
}

interface FlowchartNodeMotion {
  opacity: number;
  transform: string;
}

export interface FlowchartNodeViewModel {
  accent: string;
  borderWidth: number;
  boxShadow: string;
  fill: string;
  iconColor: string;
  isSelected: boolean;
  isUnresolved: boolean;
  opacity: number;
  shape: GraphNodeShapeDescriptor;
  textColor: string;
  transform: string;
  transition: string;
}

export function resolveFlowchartNodeViewModel(input: {
  data: FlowchartNodeData;
  didEnter: boolean;
  hoverState: FlowchartHoverState;
}): FlowchartNodeViewModel {
  const activity = resolveFlowchartNodeActivity({
    hoverState: input.hoverState,
    nodeId: input.data.nodeId,
  });
  const resolved = resolveFlowchartNodeColors({
    resolvedStyle: input.data.resolvedStyle,
    labelOrigin: input.data.labelOrigin,
    accentColor: input.data.accentColor,
  });
  const { accent, fill, textColor } = resolveFlowchartRoleColors({
    role: input.data.role,
    resolved,
  });
  const motion = resolveFlowchartNodeMotion({
    didEnter: input.didEnter,
    isActive: activity.isActive,
    isEmphasized: activity.isEmphasized,
  });

  return {
    accent,
    borderWidth: activity.isEmphasized ? EMPHASIZED_BORDER_WIDTH : RESTING_BORDER_WIDTH,
    boxShadow: buildFlowchartBoxShadow({
      accent,
      isEmphasized: activity.isEmphasized,
      isSelected: activity.isSelected,
      supportsInsetRings: shapeSupportsInsetRings(input.data.shape),
      restingGlow: input.data.role === FlowchartNodeRole.Start,
    }),
    fill,
    iconColor: resolveFlowchartIconColor(accent),
    isSelected: activity.isSelected,
    isUnresolved: input.data.labelOrigin === FlowchartLabelOrigin.Unresolved,
    opacity: motion.opacity,
    shape: getFlowchartShapeDescriptor(input.data.shape),
    textColor,
    transform: motion.transform,
    transition: FLOWCHART_NODE_TRANSITION,
  };
}

function resolveFlowchartNodeActivity(input: {
  hoverState: FlowchartHoverState;
  nodeId: string;
}): FlowchartNodeActivity {
  const activeNodeIds = getActiveNodeIds(input.hoverState);

  return {
    isActive: activeNodeIds?.has(input.nodeId) ?? true,
    isEmphasized:
      input.hoverState.hoveredNodeId === input.nodeId || input.hoverState.focusedNodeId === input.nodeId,
    isSelected: input.hoverState.focusedNodeId === input.nodeId,
  };
}

function getActiveNodeIds(hoverState: FlowchartHoverState): ReadonlySet<string> | null {
  if (hoverState.hoveredNodeId != null) {
    return hoverState.hoveredConnectedNodeIds;
  }

  if (hoverState.focusedNodeId != null) {
    return hoverState.focusedNodeIds;
  }

  return null;
}

function resolveFlowchartNodeMotion(input: {
  didEnter: boolean;
  isActive: boolean;
  isEmphasized: boolean;
}): FlowchartNodeMotion {
  if (!input.didEnter) {
    return {
      opacity: HIDDEN_OPACITY,
      transform: REST_SCALE,
    };
  }

  const scale = input.isEmphasized ? EMPHASIZED_NODE_SCALE : RESTING_NODE_SCALE;

  return {
    opacity: getFlowchartNodeOpacity({ isActive: input.isActive }),
    transform: `scale(${scale})`,
  };
}
